import React from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PeopleIcon from '@mui/icons-material/People';
import InventoryIcon from '@mui/icons-material/Inventory';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { DashboardService } from '../services/DashboardService';
import { CACHE_KEYS } from '../services/cache/cacheKeys';
import { CACHE_TTL } from '../services/cache/dataCache';
import { useCachedQuery } from '../hooks/useCachedQuery';
import PageHeader from '../components/ui/PageHeader';
import StatCard from '../components/ui/StatCard';

const Dashboard: React.FC = () => {
  const { data: stats, loading, refetch } = useCachedQuery(
    CACHE_KEYS.dashboardStats,
    DashboardService.fetchStats,
    { ttlMs: CACHE_TTL.dashboard }
  );

  const handleRefresh = () => refetch(true);

  const statCards = [
    {
      label: 'Kullanıcı Sayısı',
      value: stats?.users ?? null,
      icon: <PeopleIcon />,
      accentColor: '#3B82F6',
    },
    {
      label: 'Toplam Ürün',
      value: stats?.products ?? null,
      icon: <InventoryIcon />,
      accentColor: '#10B981',
      footer: stats?.lastProductId != null ? `Son eklenen ID: ${stats.lastProductId}` : undefined,
    },
    {
      label: 'Bekleyen Ürünler',
      value: stats?.pending_products ?? null,
      icon: <HourglassEmptyIcon />,
      accentColor: '#F59E0B',
    },
    {
      label: 'Kullanıcı Ürünleri',
      value: stats?.user_products ?? null,
      icon: <ShoppingBasketIcon />,
      accentColor: '#8B5CF6',
    },
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <PageHeader
        title="Dashboard"
        subtitle={
          stats
            ? `Platform genelindeki özet metrikler · Son güncelleme: ${new Date(stats.fetchedAt).toLocaleTimeString('tr-TR')}`
            : 'Platform genelindeki özet metrikler'
        }
        icon={<DashboardIcon />}
        actions={
          <Tooltip title="Verileri yenile">
            <span>
              <IconButton onClick={handleRefresh} disabled={loading} size="small">
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        }
      />

      {loading && !stats && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Veriler yükleniyor...
        </Typography>
      )}

      <Box
        sx={{
          display: 'grid',
          width: '100%',
          gridTemplateColumns: {
            md: 'repeat(2, minmax(0, 1fr))',
            xl: 'repeat(4, minmax(0, 1fr))',
          },
          gap: 3,
        }}
      >
        {statCards.map((card) => (
          <StatCard
            key={card.label}
            label={card.label}
            value={card.value}
            icon={card.icon}
            accentColor={card.accentColor}
            footer={card.footer}
          />
        ))}
      </Box>
    </Box>
  );
};

export default Dashboard;
